import React, { useState, useRef, useEffect, Fragment, useMemo } from 'react';
import {
  Checkbox,
  Form,
  Input,
  Select,
  Radio,
  ConfigProvider,
} from '@arco-design/web-react';
import type { DatePickerProps } from '@arco-design/web-react';
import enUS from '@arco-design/web-react/es/locale/en-US';
import MonthDatePicker from '@/components/MonthDatePicker/index';
import { terminal } from '@/constant/index';
import debug from 'debug';
import phoneCode from '../../assets/js/phone_code';
import { getCompanyStaticUrl } from '../../utils/index';
import { copyToClipboard } from '../../utils/clipboard';

import type { Popup } from '@/types/Form';
import {
  CARTSEE_TARGET_REDIRECT,
  EVENTS,
  STORAGE_KEY,
} from '@/constant/constant';
import { getLocal, getSession, setLocal, removeLocal } from '@/utils/storage';
import { commonApi, companyUUID, getShopifyUserId } from '@/service/api';
import { isEnableEventCallback } from '@/utils/experiment';
import { isShopifyOrTest } from '@/utils/platform';
import {
  checkEmailOrPhone,
  getContactStatus,
} from '@/service/strategy/request';
import { formEventV2 } from '@/service/formEventV2';
import { shopify, xike } from '@/constant/company';
import { PrivacyFieldErrorHint } from '../PrivacyFieldErrorHint';
import {
  triggerSubscribedCallback,
  triggerViewedCallback,
} from '@/utils/popupCallbacks';
import { reportFormSuccess } from '@/service/formSuccess';
import {
  getMiddlePageValues,
  getMiddlePageFormList,
  getMiddleScopedValue,
  isMiddlePage,
  type MiddlePageValue,
} from '@/utils/formPages';

// biome-ignore format: 构建问题，不能格式化
const LazyDatePicker = React.lazy(() => import(/* webpackChunkName: "269" */ '@arco-design/web-react/es/DatePicker'));
const DatePicker = (props: DatePickerProps) => (
  <ConfigProvider locale={enUS}>
    <React.Suspense fallback={null}>
      <LazyDatePicker {...props} />
    </React.Suspense>
  </ConfigProvider>
);

// 确保 Arco 基础样式和业务基础样式都会被加载（嵌入弹窗不走 App.tsx 渲染）
// 使用动态 import 以便 MiniCssExtractPlugin runtime 能自动加载（SDK 不具备 HTML link 能力）
// @ts-ignore
// biome-ignore format: 构建问题，不能格式化
import(/* webpackChunkName: "cartAnimateCss" */ '@arco-design/web-react/dist/css/arco.css');

const log = debug('cartsee:embedded:popup');

interface EmbeddedPopupProps {
  popupConfig: Popup;
}

const EmbeddedPopup: React.FC<EmbeddedPopupProps> = ({ popupConfig }) => {
  const [form] = Form.useForm();
  const [activePageSetting, setActivePageSetting] = useState<string>('start');
  // step 用于跟踪当前步骤，用于后续可能的步骤报告功能
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [step, setStep] = useState(0);
  const popupRef = useRef(null);
  const [hasReported100View, setHasReported100View] = useState(false);
  const [hasReported100ViewStep2, setHasReported100ViewStep2] = useState(false);
  const [copiedShow, setCopiedShow] = useState(false);
  const [codeList, setcodeList] = useState([]);
  const [popupPhoneWidth, setPopupPhoneWidth] = useState<number | undefined>(
    undefined,
  );
  // triggerProps 用于控制手机号选择器的弹出位置，setTriggerProps 保留用于未来可能的动态调整
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [triggerProps, setTriggerProps] = useState<any>({});
  const [formFields, setFormFields] = useState<any>({});
  const [privacyValidateFailed, setPrivacyValidateFailed] = useState(false);
  const [privacyErrorVisibleMap, setPrivacyErrorVisibleMap] = useState<
    Record<string, boolean | undefined>
  >({});
  const successReportedRef = useRef(false);
  const lastSubmitStepRef = useRef(1);

  const getCustomFieldName = (index: number) => {
    return activePageSetting && activePageSetting !== 'start'
      ? `allPropertyList_${activePageSetting}_${index}`
      : `allPropertyList${index}`;
  };

  const buildCustomPropertyItem = (item: any, value: any) => {
    const propertyItem = {
      ...item,
      optionList: Array.isArray(item?.optionList)
        ? item.optionList.map((option) => ({ ...option, selected: false }))
        : item?.optionList,
    };

    if (!propertyItem?.routine && propertyItem?.valueType === 3) {
      propertyItem.value = formatCustomDateValue(
        value,
        propertyItem.formatString,
      );
      return propertyItem;
    }

    if (Array.isArray(propertyItem?.optionList)) {
      propertyItem.optionList = propertyItem.optionList.map((option) => ({
        ...option,
        selected: Array.isArray(value)
          ? value.includes(option.id)
          : option.id === value,
      }));
    }

    return propertyItem;
  };

  const formatCustomDateValue = (value: any, formatString?: string) => {
    if (value == null || value === '') {
      return value;
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value?.format === 'function') {
      const fmt = formatString === 'MM-dd' ? 'MM-DD' : 'YYYY-MM-DD';
      return value.format(fmt);
    }
    return String(value);
  };

  const { id, name, settings, triggerEventType } = popupConfig;

  const middlePageValues = useMemo(() => {
    return getMiddlePageValues(settings);
  }, [settings]);
  const currentMiddlePageValue: MiddlePageValue | undefined = isMiddlePage(
    activePageSetting,
  )
    ? (activePageSetting as MiddlePageValue)
    : undefined;

  // 监听表单字段变化
  const code = Form.useWatch('code', form);
  const country = Form.useWatch('country', form);
  const phoneCountry = Form.useWatch('phoneCountry', form);

  // 全局变量
  let teleInputFlag = true;

  // 获取手机号国家代码列表
  const getCodeList = () => {
    commonApi(
      {
        companyUUID: companyUUID,
      },
      'form/phone/codeLists',
    )
      .then((response) => {
        const companyStaticUrl = getCompanyStaticUrl();
        let codes = response.data?.data?.map((pCode, idx) => {
          return {
            code: pCode?.phoneCode ? pCode?.phoneCode : pCode ? pCode : '',
            country: pCode?.countryAbbreviation
              ? pCode?.countryAbbreviation
              : `${idx}`,
            countryName: getFlagLocal(pCode?.countryAbbreviation),
            src: `${companyStaticUrl}Public/cartsee/formImage/${(pCode?.countryAbbreviation ?? '').trim().toLocaleLowerCase()}.svg`,
          };
        });
        codes = codes.sort((a, b) => {
          return a.countryName.localeCompare(b.countryName);
        });
        setcodeList(codes);
      })
      .catch((e) => {
        log('获取手机号代码列表失败', e);
      });
  };

  // 获取国家名称
  const getFlagLocal = (country) => {
    if (!country) return '';
    const flag = phoneCode.find((item) => {
      return `${item.LOCAL}` === country;
    });
    if (flag) {
      return `${flag.COUNTRY}`.trim();
    }
    return '';
  };

  // 初始化手机号代码列表
  useEffect(() => {
    getCodeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 添加可见性检测
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 当元素100%可见时
          if (entry.intersectionRatio === 1) {
            const info = getLocal(STORAGE_KEY.AB_MAP);
            let abMapping = null;
            if (info) {
              try {
                abMapping = JSON.parse(info);
              } catch (error) {}
            }

            // 第一步曝光监控
            if (activePageSetting === 'start' && !hasReported100View) {
              log(
                'embedded popup view step=1 intersectionRatio=%s',
                entry.intersectionRatio,
              );
              commonApi(
                {
                  cid: getLocal(STORAGE_KEY.CID) || null,
                  formUid: getLocal(STORAGE_KEY.FORM_UID) || null,
                  pathname: window.location.pathname,
                  event: 'view',
                  companyUUID: companyUUID,
                  shopifyUserId: getShopifyUserId(),
                  userAgent: navigator.userAgent,
                  formId: id,
                  step: 1,
                  terminal: terminal ? 'pc' : 'mobile',
                  abMapping: abMapping,
                },
                'form/out/view',
              )
                .then((response) => {})
                .catch((e) => {});

              // 监听曝光的埋点
              formEventV2({
                event: 'popup_view',
                triggerEventType,
                showFrom: getLocal(STORAGE_KEY.SHOW_FROM) || '',
                formId: id,
                formName: name,
                step: 1,
                templateId: popupConfig?.templateId,
                pixel:
                  window.pageYOffset ||
                  document.documentElement.scrollTop ||
                  document.body.scrollTop,
                leaveTriggerType:
                  triggerEventType === 1
                    ? getSession(STORAGE_KEY.LEAVE_TRIGGER_TYPE)
                    : '',
              })
                .then((response) => {})
                .catch((e) => {});
              triggerViewedCallback({
                popupType: popupConfig?.type,
                popupName: name,
                triggerEventType,
              });
              // 设置为已上报100%可见
              setHasReported100View(true);
            }

            // 中间步骤曝光监控
            if (isMiddlePage(activePageSetting) && !hasReported100ViewStep2) {
              log(
                'embedded popup view step=1 intersectionRatio=%s',
                entry.intersectionRatio,
              );
              commonApi(
                {
                  cid: getLocal(STORAGE_KEY.CID) || null,
                  formUid: getLocal(STORAGE_KEY.FORM_UID) || null,
                  pathname: window.location.pathname,
                  event: 'view',
                  companyUUID: companyUUID,
                  shopifyUserId: getShopifyUserId(),
                  userAgent: navigator.userAgent,
                  formId: id,
                  step: 1,
                  terminal: terminal ? 'pc' : 'mobile',
                  abMapping: abMapping,
                },
                'form/out/view',
              )
                .then((response) => {})
                .catch((e) => {});

              // 监听曝光的埋点
              formEventV2({
                event: 'popup_view',
                triggerEventType,
                showFrom: getLocal(STORAGE_KEY.SHOW_FROM) || '',
                formId: id,
                formName: name,
                step: 1,
                templateId: popupConfig?.templateId,
                pixel:
                  window.pageYOffset ||
                  document.documentElement.scrollTop ||
                  document.body.scrollTop,
                leaveTriggerType:
                  triggerEventType === 1
                    ? getSession(STORAGE_KEY.LEAVE_TRIGGER_TYPE)
                    : '',
              })
                .then((response) => {})
                .catch((e) => {});
              // 设置为已上报100%可见
              setHasReported100View(true);
            }

            // 第二步曝光监控
            if (isMiddlePage(activePageSetting) && !hasReported100ViewStep2) {
              log(
                'embedded popup view step=2 intersectionRatio=%s',
                entry.intersectionRatio,
              );
              commonApi(
                {
                  cid: getLocal(STORAGE_KEY.CID) || null,
                  formUid: getLocal(STORAGE_KEY.FORM_UID) || null,
                  pathname: window.location.pathname,
                  event: 'view',
                  companyUUID: companyUUID,
                  shopifyUserId: getShopifyUserId(),
                  userAgent: navigator.userAgent,
                  formId: id,
                  step: 2,
                  terminal: terminal ? 'pc' : 'mobile',
                  abMapping: abMapping,
                },
                'form/out/view',
              )
                .then((response) => {})
                .catch((e) => {});

              // 监听曝光的埋点
              formEventV2({
                event: 'popup_view',
                triggerEventType,
                showFrom: getLocal(STORAGE_KEY.SHOW_FROM) || '',
                formId: id,
                formName: name,
                step: 2,
                templateId: popupConfig?.templateId,
                pixel:
                  window.pageYOffset ||
                  document.documentElement.scrollTop ||
                  document.body.scrollTop,
                leaveTriggerType:
                  triggerEventType === 1
                    ? getSession(STORAGE_KEY.LEAVE_TRIGGER_TYPE)
                    : '',
              })
                .then((response) => {})
                .catch((e) => {});
              // 设置为已上报100%可见
              setHasReported100ViewStep2(true);
            }
          }
        });
      },
      { threshold: 1.0 }, // 阈值设为1.0表示100%可见时触发
    );

    const currentRef = popupRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    id,
    name,
    triggerEventType,
    hasReported100View,
    hasReported100ViewStep2,
    activePageSetting,
    popupConfig.templateId,
  ]);

  // 根据 activePageSetting 状态获取对应的 settings 配置
  const stateSettings = useMemo(() => {
    const settingsConfigs: Record<string, any> = {
      start: {
        texts: {
          topTitle: settings.texts?.topTitle || '',
          topSubTitle: settings.texts?.topSubTitle || '',
          mainButton: settings.texts?.mainButton || '',
          subButton: settings.texts?.subButton || '',
        },
        rules: settings.rules,
        styles: settings.styles,
        formList: settings.start || [],
      },
      end: {
        texts: {
          topTitle: settings.texts?.endTopTitle || '',
          topSubTitle: settings.texts?.endTopSubTitle || '',
          mainButton: settings.texts?.endMainButton || '',
          subButton: settings.texts?.endSubButton || '',
        },
        rules: settings.rules,
        styles: settings.styles,
        formList: settings.end || [],
      },
    };

    if (isMiddlePage(activePageSetting)) {
      const mpv = activePageSetting as MiddlePageValue;
      settingsConfigs[activePageSetting] = {
        texts: {
          topTitle:
            getMiddleScopedValue(settings.texts, mpv, 'middleTopTitle') ||
            settings.texts?.topTitle ||
            '',
          topSubTitle:
            getMiddleScopedValue(settings.texts, mpv, 'middleTopSubTitle') ||
            settings.texts?.topSubTitle ||
            '',
          mainButton:
            getMiddleScopedValue(settings.texts, mpv, 'middleMainButton') ||
            settings.texts?.mainButton ||
            '',
          subButton:
            getMiddleScopedValue(settings.texts, mpv, 'middleSubButton') ||
            settings.texts?.subButton ||
            '',
        },
        rules: {
          ...settings.rules,
          inputType: getMiddleScopedValue(
            settings.rules,
            mpv,
            'middleInputType',
          ),
          inputBorderStyle: getMiddleScopedValue(
            settings.rules,
            mpv,
            'middleInputBorderStyle',
          ),
          inputBorderColor: getMiddleScopedValue(
            settings.rules,
            mpv,
            'middleInputBorderColor',
          ),
          inputBorderRadius: getMiddleScopedValue(
            settings.rules,
            mpv,
            'middleInputBorderRadius',
          ),
          inputBackgroundShow: getMiddleScopedValue(
            settings.rules,
            mpv,
            'middleInputBackgroundShow',
          ),
          inputBackground: getMiddleScopedValue(
            settings.rules,
            mpv,
            'middleInputBackground',
          ),
          inputColor: getMiddleScopedValue(
            settings.rules,
            mpv,
            'middleInputColor',
          ),
          dropdownTitleColor:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownTitleColor',
            ) ?? settings.rules?.dropdownTitleColor,
          dropdownTitleFontSize:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownTitleFontSize',
            ) ?? settings.rules?.dropdownTitleFontSize,
          dropdownTitleFontFamily:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownTitleFontFamily',
            ) ?? settings.rules?.dropdownTitleFontFamily,
          dropdownBorderStyle:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownBorderStyle',
            ) ?? settings.rules?.dropdownBorderStyle,
          dropdownBorderColor:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownBorderColor',
            ) ?? settings.rules?.dropdownBorderColor,
          dropdownBorderRadius:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownBorderRadius',
            ) ?? settings.rules?.dropdownBorderRadius,
          dropdownBackgroundType:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownBackgroundType',
            ) ?? settings.rules?.dropdownBackgroundType,
          dropdownBackgroundColor:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownBackgroundColor',
            ) ?? settings.rules?.dropdownBackgroundColor,
          dropdownOptionTextColor:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownOptionTextColor',
            ) ?? settings.rules?.dropdownOptionTextColor,
          dropdownMarginTop:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownMarginTop',
            ) ?? settings.rules?.dropdownMarginTop,
          dropdownMarginBottom:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownMarginBottom',
            ) ?? settings.rules?.dropdownMarginBottom,
          dropdownMarginLeft:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownMarginLeft',
            ) ?? settings.rules?.dropdownMarginLeft,
          dropdownMarginRight:
            getMiddleScopedValue(
              settings.rules,
              mpv,
              'middleDropdownMarginRight',
            ) ?? settings.rules?.dropdownMarginRight,
        },
        styles: settings.styles,
        formList: getMiddlePageFormList(settings, mpv),
      };
    }

    return settingsConfigs[activePageSetting] || settingsConfigs.start;
  }, [activePageSetting, settings]);

  // 设置默认手机号国家代码
  useEffect(() => {
    stateSettings.formList.forEach((item) => {
      if (item.type === 'Mobile Number') {
        // 增加后端配置：ip识别 > 弹窗配置
        let ipCode;
        let ipCountryCode;

        ipCountryCode = getSession(STORAGE_KEY.IP_COUNTRY_CODE);
        if (codeList.length > 0) {
          ipCode = codeList.find((item) => {
            return item.country === ipCountryCode;
          })?.code;
        } else {
          // 兜底配置表
          ipCode = phoneCode.find((item) => {
            return item.LOCAL === ipCountryCode;
          })?.CODE;
          if (ipCode) {
            ipCode = '+' + ipCode;
          }
        }
        log('ip识别 ipCode=%s ipCountryCode=%s', ipCode, ipCountryCode);

        if ([shopify[30], xike[529]].includes(companyUUID)) {
          form.setFieldValue(
            'phoneCountry',
            ipCountryCode || item?.phoneCountryAbbreviation,
          );
        } else {
          form.setFieldValue(
            'country',
            ipCountryCode || item?.phoneCountryAbbreviation,
          );
        }
        form.setFieldValue('code', ipCode || item?.phoneCode);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateSettings.formList, codeList]);

  // 当国家选择变化时，更新代码
  useEffect(() => {
    log('country=%s, phoneCountry=%s', country, phoneCountry);
    const realItem = codeList.find((item) => {
      return [shopify[30], xike[529]].includes(companyUUID)
        ? item.country === phoneCountry
        : item.country === country;
    });
    if (realItem && realItem.code) {
      form.setFieldValue('code', realItem.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, phoneCountry, codeList]);

  // 隔离 styleMap 获取逻辑，参考 tanChuang 的实现方式
  const getStyleMapValue = (
    baseKey: string,
    property: string,
    useWrapperKey = false,
  ): string | undefined => {
    const styleMap = settings.styles.styleMap;

    let key: string;
    if (activePageSetting === 'start') {
      key = useWrapperKey ? `cartx-b-show .${baseKey}` : baseKey;
    } else if (isMiddlePage(activePageSetting)) {
      const middleKey = `${baseKey}__${activePageSetting}`;
      // 如果当前步骤的样式不存在，回退到 __middle 或 baseKey
      if (!styleMap[middleKey]?.[property]) {
        const fallbackMiddle = `${baseKey}__middle`;
        if (styleMap[fallbackMiddle]?.[property]) {
          key = useWrapperKey
            ? `cartx-b-show .${fallbackMiddle}`
            : fallbackMiddle;
        } else {
          key = useWrapperKey ? `cartx-b-show .${baseKey}` : baseKey;
        }
      } else {
        key = useWrapperKey ? `cartx-b-show .${middleKey}` : middleKey;
      }
    } else {
      // end
      const endKey = `${baseKey}-end`;
      key = useWrapperKey ? `cartx-b-show .${endKey}` : endKey;
    }

    // 如果使用 wrapperKey 但找不到，尝试不使用 wrapperKey
    if (useWrapperKey && !styleMap[key]?.[property]) {
      const fallbackKey =
        activePageSetting === 'start'
          ? baseKey
          : isMiddlePage(activePageSetting)
            ? `${baseKey}__${activePageSetting}`
            : `${baseKey}-end`;
      return styleMap[fallbackKey]?.[property];
    }

    return styleMap[key]?.[property];
  };

  const handleFormButtonClick = async () => {
    if (activePageSetting === 'start') {
      form
        .validate()
        .then(() => {
          setPrivacyValidateFailed(false);
          if (settings.rules.buttonAction === 'link') {
            handleSignup(1);
            window.open(
              settings.rules.openUrl,
              settings.rules.openUrl.includes(CARTSEE_TARGET_REDIRECT)
                ? '_self'
                : 'target',
            );
          } else if (middlePageValues.length > 0) {
            // 有中间页，先提交第一步，然后跳转到第一个中间页
            handleSignup(1);
            setStep(1);
            setActivePageSetting(middlePageValues[0]);
          } else {
            // 没有中间页，直接提交并跳转到结束页
            handleSignup(1);
            setActivePageSetting('end');
          }
        })
        .catch((error) => {
          setPrivacyValidateFailed(true);
        });
    } else if (isMiddlePage(activePageSetting)) {
      const currentIdx = middlePageValues.indexOf(activePageSetting);
      const nextMiddle =
        currentIdx >= 0 && currentIdx < middlePageValues.length - 1
          ? middlePageValues[currentIdx + 1]
          : undefined;
      const middleButtonAction = getMiddleScopedValue(
        settings.rules,
        currentMiddlePageValue,
        'middleButtonAction',
      );
      const middleOpenUrl = getMiddleScopedValue(
        settings.rules,
        currentMiddlePageValue,
        'middleOpenUrl',
      );

      form
        .validate()
        .then(() => {
          if (middleButtonAction === 'link' && middleOpenUrl) {
            handleSignup(currentIdx + 2);
            window.open(
              middleOpenUrl,
              middleOpenUrl.includes(CARTSEE_TARGET_REDIRECT)
                ? '_self'
                : 'target',
            );
          } else if (nextMiddle) {
            // 还有下一个中间页
            setPrivacyValidateFailed(false);
            handleSignup(currentIdx + 2);
            setStep(currentIdx + 2);
            setActivePageSetting(nextMiddle);
          } else {
            setPrivacyValidateFailed(false);
            // 最后一个中间页，跳转到结束页
            handleSignup(currentIdx + 2);
            setActivePageSetting('end');
          }
        })
        .catch((error) => {
          setPrivacyValidateFailed(true);
        });
    } else {
      // end 页面
      if (settings.rules.endButtonAction === 'link') {
        window.open(
          settings.rules.endOpenUrl,
          settings.rules.endOpenUrl.includes(CARTSEE_TARGET_REDIRECT)
            ? '_self'
            : 'target',
        );
      }
    }
  };

  useEffect(() => {
    if (activePageSetting !== 'end') {
      successReportedRef.current = false;
      return;
    }
    if (successReportedRef.current) {
      return;
    }

    const formValues = form.getFieldsValue();
    reportFormSuccess({
      formId: id,
      email: formValues.email || formFields.email || '',
      phoneCode: formValues.code || formFields.code || '',
      phone: formValues.phone || formValues.Number || '',
      step: lastSubmitStepRef.current,
    }).catch(() => {});

    successReportedRef.current = true;
  }, [activePageSetting, form, formFields, id]);

  const handleCouponCopy = async () => {
    const copied = await copyToClipboard(settings.rules.couponCode || '');
    if (copied) {
      setCopiedShow(true);
      setTimeout(() => {
        setCopiedShow(false);
      }, 1000);
    } else {
      log('copy coupon failed');
    }
    // 埋点
    formEventV2({
      event: 'popup_codecopy',
      triggerEventType,
      formId: id,
      formName: name,
      code: settings.rules.couponCode,
    })
      .then((response) => {
        // console.log('数据上报成功')
      })
      .catch((e) => {});
  };

  const handleSignup = (submitStep: number = 1) => {
    lastSubmitStepRef.current = submitStep;
    const currentValues = form.getFieldsValue();
    setFormFields({
      ...formFields,
      ...currentValues,
    });
    // signup 清除悬浮窗
    removeLocal(STORAGE_KEY.TEMP_POP);
    const formValues = currentValues;
    const { email, acceptsMarketing } = formValues;

    log('handleSignup submit email=%s step=%d', email, submitStep);

    let phone: string | null = null;
    if (formValues.Number || formFields.Number) {
      phone =
        (formValues.code || formFields.code) +
        (formValues.Number || formFields.Number);
    } else {
      phone = null;
    }

    formEventV2({
      event: 'popup_submit',
      triggerEventType,
      showFrom: getLocal(STORAGE_KEY.SHOW_FROM) || '',
      formId: id,
      formName: name,
      step: submitStep,
      templateId: popupConfig?.templateId,
      inputType: email ? 'email' : 'phone',
      email: email || formFields.email || '',
      code: formValues.code || formFields.code || '',
      phone: phone,
    })
      .then((response) => {
        // console.log('数据上报成功')
      })
      .catch((e) => {});

    const currentFormList = stateSettings.formList || [];
    const isEmailPrivacyShow = currentFormList.some(
      (item) => item.type === 'Email' && item.enablePrivacyShow === true,
    );
    // 必须用整表原 index 拼字段名（与渲染一致）；filter 后再 map 会让自定义字段 index 错位导致答案丢失
    const allPropertyList = currentFormList
      .map((item, index) => {
        if (item?.type !== '自定义') return undefined;
        const fieldName = getCustomFieldName(index);
        const value = Object.prototype.hasOwnProperty.call(
          formValues,
          fieldName,
        )
          ? formValues[fieldName]
          : formFields[fieldName];
        return buildCustomPropertyItem(item, value);
      })
      .filter((item) => typeof item !== 'undefined');

    commonApi(
      {
        cid: getLocal(STORAGE_KEY.CID) || null,
        formUid: getLocal(STORAGE_KEY.FORM_UID) || null,
        event: 'signup',
        companyUUID: companyUUID,
        shopifyUserId: getShopifyUserId(),
        email: email || formFields.email || '',
        formId: id,
        loginEmail: (window as any)?.cartq?.params?.customer?.email || '',
        phoneCode: formValues.code || formFields.code || '',
        phone: phone,
        lastName: '',
        firstName: '',
        birthday: '',
        city: '',
        state: '',
        province: '',
        country: '',
        userAgent: navigator?.userAgent,
        allPropertyList,
        ...(isEmailPrivacyShow ? { acceptsMarketing } : {}),
        step: submitStep,
        pathname: window.location.pathname,
        terminal: terminal ? 'pc' : 'mobile',
      },
      'form/out/signup',
    )
      .then((response) => {
        setLocal(
          STORAGE_KEY.CARTX_CUSTOMER,
          JSON.stringify({
            email: email || '',
            phone: formValues.phone || formValues.Number || '',
          }),
        );
        window?.postMessage('_cartsee_send');

        // 触发配置存储事件
        if (isEnableEventCallback()) {
          window.dispatchEvent(
            new CustomEvent(EVENTS.POPUP.SIGNUP, {
              detail: {
                cid: getLocal(STORAGE_KEY.CID) || null,
                companyUUID: companyUUID,
                shopifyUserId: getShopifyUserId(),
                formId: id,
                event: 'signup',
                email: email || '',
                phoneCode: formValues.code || '',
                phone: formValues.phone || formValues.Number || '',
                step: submitStep,
              },
            }),
          );
        }

        if (email) {
          triggerSubscribedCallback({
            type: 'email',
            email,
          });
        }

        if (response?.data?.data?.cid) {
          setLocal(STORAGE_KEY.CID, response.data.data.cid);
        } else if (response?.data?.data?.formUid) {
          setLocal(STORAGE_KEY.FORM_UID, response.data.data.formUid);
        }

        if (response?.data?.code === 0) {
          if (response?.data?.data?.couponCode) {
            if (isShopifyOrTest()) {
              fetch(
                window.location.origin +
                  `/discount/${response?.data?.data?.couponCode}`,
              );
            }
          }

          if (isEnableEventCallback() && response?.data?.data?.couponCode) {
            window.dispatchEvent(
              new CustomEvent(EVENTS.POPUP.COUPON, {
                detail: {
                  cid: getLocal(STORAGE_KEY.CID) || null,
                  companyUUID: companyUUID,
                  shopifyUserId: getShopifyUserId(),
                  formId: id,
                  event: 'coupon',
                  couponCode: response?.data?.data?.couponCode,
                  step: 1,
                },
              }),
            );
          }
        }
      })
      .catch((e) => {});
  };

  const hidePrivateCheckout = true && popupConfig?.type === 'POP_WINDOW';

  const PrivacyBox = ({ item }) => {
    const privacyWrapStyle: React.CSSProperties = {
      marginBottom: 0,
      fontSize: settings.styles.styleMap['cartx-b-privacy']?.['font-size'],
      lineHeight: '16px',
      display: 'inline-block',
      textAlign: 'left',
    };
    const privacyStyle = {
      color: 'inherit',
      fontSize: settings.styles.styleMap['cartx-b-privacy']?.['font-size'],
      lineHeight: '16px',
      textDecoration: 'underline',
    };
    let { text, linkText, privacyPolicyLink, link2Text, privacyPolicyLink2 } =
      item.type === 'Email' ? item : popupConfig;
    return (
      <span className="cartx-checkbox-set-text" style={privacyWrapStyle}>
        {text}
        <a
          rel="noreferrer"
          target="_blank"
          href={privacyPolicyLink}
          style={privacyStyle}
        >
          {linkText}
        </a>
        {link2Text ? (
          <Fragment>
            &nbsp;{item.joinText || 'and'}&nbsp;
            <a
              rel="noreferrer"
              target="_blank"
              href={privacyPolicyLink2}
              style={privacyStyle}
            >
              {link2Text}
            </a>
            .
          </Fragment>
        ) : (
          ''
        )}
      </span>
    );
  };

  const PrivacyBlock = () => {
    const currentList = stateSettings.formList || [];
    if (!currentList || currentList.length <= 0) {
      return null;
    }
    return currentList.map((item, index) => {
      const privacyFieldName =
        item.type === 'Email' ? 'acceptsMarketing' : 'agreePrivacyPolicy';
      const isPrivacyRequired = ['required', 1, '1', true].includes(
        item?.privacyCheckRequired,
      );
      const privacyValue = form?.getFieldValue(privacyFieldName) || '';
      const privacyChecked = !!privacyValue;
      const explicitError = privacyErrorVisibleMap?.[privacyFieldName];
      const showPrivacyError =
        isPrivacyRequired &&
        (explicitError ?? (privacyValidateFailed && !privacyChecked));
      if (
        // 邮箱enablePrivacyShow=true为展示
        (item.type === 'Email' && item.enablePrivacyShow !== true) ||
        !item.privacyPolicyLink
      ) {
        return null;
      }
      return (
        <Fragment key={item.type + index}>
          <div className="cartx-privacy-new-wrapper">
            <div className="cartx-privacy-new-inner">
              <div
                className={
                  hidePrivateCheckout
                    ? 'cartx-form cartx-privacy-box cartx-privacy-box-new cartx-privacy-no-check-box'
                    : 'cartx-form cartx-privacy-box cartx-privacy-box-new'
                }
                style={{ maxHeight: '43px', overflow: 'auto' }}
                data-position={settings?.rules?.privacyPosition}
              >
                <Form wrapperCol={{ span: 24 }} form={form}>
                  <Form.Item
                    field={privacyFieldName}
                    initialValue={
                      (!item.enablePrivacyCheck ||
                        item.enablePrivacyCheckValue === 'on') &&
                      hidePrivateCheckout
                        ? true
                        : false
                    }
                    className="cartx-option-columns cartx-b-privacy-new"
                    style={{ width: '100%' }}
                    rules={[
                      {
                        validator: (value, callback) => {
                          if (isPrivacyRequired && !value) callback(' ');
                          else callback();
                        },
                      },
                    ]}
                  >
                    {!item.enablePrivacyCheck && hidePrivateCheckout ? (
                      <div>
                        <PrivacyBox item={item} />
                      </div>
                    ) : (
                      <Checkbox
                        defaultChecked={
                          form.getFieldValue(privacyFieldName) !== undefined
                            ? form.getFieldValue(privacyFieldName)
                            : item.enablePrivacyCheck &&
                                item.enablePrivacyCheckValue === 'on' &&
                                hidePrivateCheckout
                              ? true
                              : false
                        }
                        style={{
                          marginLeft: 0,
                          marginTop: 0,
                          marginRight: 0,
                          paddingLeft: 0,
                          paddingTop: 0,
                        }}
                        className="cartx-checkbox-set"
                        onChange={(checked: boolean) => {
                          if (!isPrivacyRequired) return;
                          setPrivacyErrorVisibleMap((prev) => ({
                            ...prev,
                            [privacyFieldName]: !checked,
                          }));
                        }}
                      >
                        <PrivacyBox item={item} />
                      </Checkbox>
                    )}
                  </Form.Item>
                </Form>
              </div>
            </div>
          </div>
          {showPrivacyError ? (
            <PrivacyFieldErrorHint
              privacyCheckErrorTip={item?.privacyCheckErrorTip}
            />
          ) : null}
        </Fragment>
      );
    });
  };

  const rules = settings.rules;
  const styleMap = settings.styles.styleMap;

  const textAlignMap: Record<string, string> = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  return (
    <div
      className={[
        'cartx-embedded-popup',
        'cartx-embedded-popup-new',
        `cartx-embedded-popup__${activePageSetting}`,
      ].join(' ')}
      ref={popupRef}
      id={`cartx-embedded-popup-${id}`}
      style={{
        background: settings.styles.styleMap['cartx-b-show'].background,
      }}
    >
      <style>
        {`
          #cartx-embedded-popup-${id} .cartx-embedded-form .cartx-form-wrapper-phone {
            border-width: ${stateSettings.rules.inputType === 'bottom' ? '0 0 1px 0' : '1px'} !important;
            border-style: ${stateSettings.rules.inputBorderStyle} !important;
            border-color: ${stateSettings.rules.inputBorderColor} !important;
            border-radius: ${stateSettings.rules.inputBorderRadius} !important;
            background: ${stateSettings.rules.inputBackgroundShow !== 'none' ? settings.rules.inputBackground : 'none'} !important;
            color: ${stateSettings.rules.inputColor} !important;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form .cartx-embedded-form-input {
            font-size: 14px;
            border-width: ${stateSettings.rules.inputType === 'bottom' ? '0 0 1px 0' : '1px'} !important;
            border-style: ${stateSettings.rules.inputBorderStyle} !important;
            border-color: ${stateSettings.rules.inputBorderColor} !important;
            border-radius: ${stateSettings.rules.inputBorderRadius} !important;
            background: ${stateSettings.rules.inputBackgroundShow !== 'none' ? settings.rules.inputBackground : 'none'} !important;
            color: ${stateSettings.rules.inputColor} !important;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form .cartx-embedded-form-input.phone-code-flag-embedded {
            padding: 0 8px;
            border: none !important;
            display: flex;
            align-items: center;
            justify-content: center;
            padding-right: 12px;
            height: 42px;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form .cartx-embedded-form-input.phone-code-flag-embedded::after {
            content: '';
            position: absolute;
            right: 0;
            top: 15px;
            width: 1px;
            height: 16px;
            background-color: #DCDCDC;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form .cartx-embedded-form-input.phone-code-flag-embedded .arco-select-view {
            border: none !important;
            background: transparent !important;
            padding: 0;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form .cartx-embedded-form-input.phone-code-flag-embedded .arco-select-view-value {
            padding: 0;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form input::-webkit-input-placeholder {
            color: ${stateSettings.rules.inputColor} !important;
            opacity: .3;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form input:-moz-placeholder {
            color: ${stateSettings.rules.inputColor} !important;
            opacity: .3;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form input::-moz-placeholder {
            color: ${stateSettings.rules.inputColor} !important;
            opacity: .3;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form input:-ms-input-placeholder {
            color: ${stateSettings.rules.inputColor} !important;
            opacity: .3;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns{
            text-align: left;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-radio,
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-checkbox {
            min-width: ${Math.floor(100 / (rules.optionColumns || 1)) - 3}%!important;
            margin-right: 3%!important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-radio-text,
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-checkbox-text,
          #cartx-embedded-popup-${id} .cartx-option-columns .cartx-checkbox-set-text {
            color: ${rules.optionColor || '#1D2129'}!important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-checkbox-mask,
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-radio-mask{
            border-color: ${rules.optionColor || '#1D2129'}!important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-checkbox-checked .arco-checkbox-mask,
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-radio-checked .arco-radio-mask {
            border-color: ${rules.optionBackground || '#1D2129'}!important;
            background: ${rules.optionBackground || '#1D2129'}!important;
          }

          #cartx-embedded-popup-${id} .cartx-privacy-new-wrapper {
            color: ${styleMap['cartx-b-privacy']?.['color']} !important;
            background-color: ${styleMap['cartx-b-privacy']?.['background-color']} !important;
            margin-top: ${styleMap['cartx-b-privacy']?.['margin-top'] || 0}px !important;
            margin-bottom: ${styleMap['cartx-b-privacy']?.['margin-bottom'] || 0}px  !important;
            margin-left: ${styleMap['cartx-b-privacy']?.['margin-left'] || 0}px  !important;
            margin-right: ${styleMap['cartx-b-privacy']?.['margin-right'] || 0}px  !important;
            padding-top: ${styleMap['cartx-b-privacy']?.['padding-top'] || 5}px !important;
            padding-bottom: ${styleMap['cartx-b-privacy']?.['padding-bottom'] || 5}px  !important;
            padding-left: ${styleMap['cartx-b-privacy']?.['padding-left'] || 10}px  !important;
            padding-right: ${styleMap['cartx-b-privacy']?.['padding-right'] || 10}px  !important;
            min-width: unset !important;
            border-radius: 4px;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            align-items: ${textAlignMap[styleMap['cartx-b-privacy']?.['text-align'] || 'left']};
          }
          #cartx-embedded-popup-${id} .cartx-privacy-new-wrapper .cartx-privacy-new-inner {
            box-sizing: border-box;
          }
          #cartx-embedded-popup-${id} .cartx-privacy-box.cartx-privacy-box-new {
            border-radius: 0;
            padding: 0;
            background-color: unset;
            margin: 0;
          }
          #cartx-embedded-popup-${id} .cartx-privacy-box.cartx-privacy-box-new .arco-form-item-control {
            min-height: 26px;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .cartx-checkbox-set .arco-checkbox-mask-wrapper {
            margin-left: 0;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio-text,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-text,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .cartx-checkbox-set-text {
            color: ${styleMap['cartx-b-privacy']?.color}!important;
            font-family: ${styleMap['cartx-b-privacy']?.['font-family']} !important;
            padding-top: 1px;
            padding-bottom: 1px;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-mask,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio-mask {
            border-width: 1px;
            border-color: ${styleMap['cartx-b-privacy']?.color}!important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-checked .arco-checkbox-mask,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio-checked .arco-radio-mask {
            border-color: ${styleMap['cartx-b-privacy-check']?.color} !important;
            background: ${styleMap['cartx-b-privacy-check']?.color} !important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox {
            min-width: unset !important;
            margin-right: 0 !important;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form .cartx-embedded-form-input.cartx-embedded-form-input-phone {
            border: none !important;
            height: 42px;
          }

          /* middle (dynamic) */
          ${middlePageValues
            .map((mpv) => {
              const suffix = `__${mpv}`;
              const privacyKey = `cartx-b-privacy${suffix}`;
              const privacyCheckKey = `cartx-b-privacy-check${suffix}`;
              const privacyStyle =
                styleMap[privacyKey] || styleMap['cartx-b-privacy__middle'];
              const privacyCheckStyle =
                styleMap[privacyCheckKey] ||
                styleMap['cartx-b-privacy-check__middle'];
              return `
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-privacy-new-wrapper {
            color: ${privacyStyle?.['color']} !important;
            background-color: ${privacyStyle?.['background-color']} !important;
            margin-top: ${privacyStyle?.['margin-top'] || 0}px !important;
            margin-bottom: ${privacyStyle?.['margin-bottom'] || 0}px  !important;
            margin-left: ${privacyStyle?.['margin-left'] || 0}px  !important;
            margin-right: ${privacyStyle?.['margin-right'] || 0}px  !important;
            padding-top: ${privacyStyle?.['padding-top'] || 5}px !important;
            padding-bottom: ${privacyStyle?.['padding-bottom'] || 5}px  !important;
            padding-left: ${privacyStyle?.['padding-left'] || 10}px  !important;
            padding-right: ${privacyStyle?.['padding-right'] || 10}px  !important;
            align-items: ${textAlignMap[privacyStyle?.['text-align'] || 'left']};
          }
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-option-columns.cartx-b-privacy-new .arco-radio-text,
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-text,
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-option-columns.cartx-b-privacy-new .cartx-checkbox-set-text {
            color: ${privacyStyle?.color}!important;
            font-family: ${privacyStyle?.['font-family']} !important;
          }
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-mask,
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-option-columns.cartx-b-privacy-new .arco-radio-mask {
            border-color: ${privacyStyle?.color}!important;
          }
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-checked .arco-checkbox-mask,
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-option-columns.cartx-b-privacy-new .arco-radio-checked .arco-radio-mask {
            border-color: ${privacyCheckStyle?.color} !important;
            background: ${privacyCheckStyle?.color} !important;
          }
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-dropdown-select {
            border: none !important;
            background: none !important;
            ${stateSettings.rules.dropdownMarginTop !== undefined ? `margin-top: ${stateSettings.rules.dropdownMarginTop}px !important;` : ''}
            ${stateSettings.rules.dropdownMarginBottom !== undefined ? `margin-bottom: ${stateSettings.rules.dropdownMarginBottom}px !important;` : ''}
            ${stateSettings.rules.dropdownMarginLeft !== undefined ? `margin-left: ${stateSettings.rules.dropdownMarginLeft}px !important;` : ''}
            ${stateSettings.rules.dropdownMarginRight !== undefined ? `margin-right: ${stateSettings.rules.dropdownMarginRight}px !important;` : ''}
          }
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-dropdown-select .arco-select-view {
            border-width: 1px !important;
            border-style: ${typeof stateSettings.rules.dropdownBorderStyle !== 'undefined' ? stateSettings.rules.dropdownBorderStyle : 'solid'} !important;
            border-color: ${typeof stateSettings.rules.dropdownBorderColor !== 'undefined' ? stateSettings.rules.dropdownBorderColor : '#0D0C16'} !important;
            border-radius: ${typeof stateSettings.rules.dropdownBorderRadius !== 'undefined' ? stateSettings.rules.dropdownBorderRadius : '4px'} !important;
            background: ${stateSettings.rules.dropdownBackgroundType !== 'none' ? (typeof stateSettings.rules.dropdownBackgroundColor !== 'undefined' ? stateSettings.rules.dropdownBackgroundColor : '#FFFFFF') : 'none'} !important;
          }
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-dropdown-select .arco-select-view-value,
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-dropdown-select .arco-input {
            ${stateSettings.rules.dropdownOptionTextColor ? `color: ${stateSettings.rules.dropdownOptionTextColor} !important;` : ''}
          }
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-dropdown-popper .arco-select-option {
            ${stateSettings.rules.dropdownOptionTextColor ? `color: ${stateSettings.rules.dropdownOptionTextColor} !important;` : ''}
          }
          #cartx-embedded-popup-${id}.cartx-embedded-popup__${mpv} .cartx-dropdown-title {
            text-align: left !important;
            ${stateSettings.rules.dropdownTitleColor ? `color: ${stateSettings.rules.dropdownTitleColor} !important;` : ''}
            ${stateSettings.rules.dropdownTitleFontSize ? `font-size: ${stateSettings.rules.dropdownTitleFontSize} !important;` : ''}
            ${stateSettings.rules.dropdownTitleFontFamily ? `font-family: ${stateSettings.rules.dropdownTitleFontFamily} !important;` : ''}
          }
            `;
            })
            .join('')}

          /* dropdown styles (start) */
          #cartx-embedded-popup-${id} .cartx-dropdown-select {
            border: none !important;
            background: none !important;
            ${rules.dropdownMarginTop !== undefined ? `margin-top: ${rules.dropdownMarginTop}px !important;` : ''}
            ${rules.dropdownMarginBottom !== undefined ? `margin-bottom: ${rules.dropdownMarginBottom}px !important;` : ''}
            ${rules.dropdownMarginLeft !== undefined ? `margin-left: ${rules.dropdownMarginLeft}px !important;` : ''}
            ${rules.dropdownMarginRight !== undefined ? `margin-right: ${rules.dropdownMarginRight}px !important;` : ''}
          }
          #cartx-embedded-popup-${id} .cartx-dropdown-select .arco-select-view {
            border-width: 1px !important;
            border-style: ${typeof rules.dropdownBorderStyle !== 'undefined' ? rules.dropdownBorderStyle : 'solid'} !important;
            border-color: ${typeof rules.dropdownBorderColor !== 'undefined' ? rules.dropdownBorderColor : '#0D0C16'} !important;
            border-radius: ${typeof rules.dropdownBorderRadius !== 'undefined' ? rules.dropdownBorderRadius : '4px'} !important;
            background: ${rules.dropdownBackgroundType !== 'none' ? (typeof rules.dropdownBackgroundColor !== 'undefined' ? rules.dropdownBackgroundColor : '#FFFFFF') : 'none'} !important;
          }
          #cartx-embedded-popup-${id} .cartx-dropdown-select .arco-select-view-value,
          #cartx-embedded-popup-${id} .cartx-dropdown-select .arco-input {
            ${rules.dropdownOptionTextColor ? `color: ${rules.dropdownOptionTextColor} !important;` : ''}
          }
          #cartx-embedded-popup-${id} .cartx-dropdown-popper .arco-select-option {
            ${rules.dropdownOptionTextColor ? `color: ${rules.dropdownOptionTextColor} !important;` : ''}
          }
          #cartx-embedded-popup-${id} .cartx-dropdown-title {
            text-align: left !important;
            ${rules.dropdownTitleColor ? `color: ${rules.dropdownTitleColor} !important;` : ''}
            ${rules.dropdownTitleFontSize ? `font-size: ${rules.dropdownTitleFontSize} !important;` : ''}
            ${rules.dropdownTitleFontFamily ? `font-family: ${rules.dropdownTitleFontFamily} !important;` : ''}
          }
        `}
      </style>
      <div
        className="cartx-a-message-copy cartx-b-message-copy"
        style={{ display: copiedShow ? 'block' : 'none' }}
      >
        copied
      </div>
      <div className="cartx-embedded-content">
        {stateSettings.texts.topTitle !== '' && (
          <div
            className="cartx-embedded-title"
            style={{
              color: getStyleMapValue('cartx-b-title', 'color'),
              fontFamily: getStyleMapValue('cartx-b-title', 'font-family'),
              fontSize: getStyleMapValue('cartx-b-title', 'font-size'),
              textAlign: getStyleMapValue(
                'cartx-b-title',
                'text-align',
              ) as React.CSSProperties['textAlign'],
            }}
          >
            {stateSettings.texts.topTitle}
          </div>
        )}

        {stateSettings.texts.topSubTitle !== '' && (
          <div
            className="cartx-embedded-subtitle"
            style={{
              color: getStyleMapValue('cartx-b-subtitle', 'color'),
              fontFamily: getStyleMapValue('cartx-b-subtitle', 'font-family'),
              fontSize: getStyleMapValue('cartx-b-subtitle', 'font-size'),
              textAlign: getStyleMapValue(
                'cartx-b-subtitle',
                'text-align',
              ) as React.CSSProperties['textAlign'],
            }}
          >
            {stateSettings.texts.topSubTitle}
          </div>
        )}

        {(activePageSetting === 'start' || isMiddlePage(activePageSetting)) && (
          <div className="cartx-embedded-form">
            <Form form={form} layout="vertical">
              {stateSettings.formList.map((item, index) => {
                if (item.routine && item.type === 'Email') {
                  return (
                    <div className="cartx-form-wrapper" key={'form' + index}>
                      <Form.Item
                        style={{
                          marginBottom: 0,
                        }}
                        label=""
                        field="email"
                        key={index}
                        validateTrigger={'onSubmit'}
                        rules={[
                          {
                            required: item.necessary,
                            message:
                              popupConfig.tipEmptyEmailText ??
                              'Please enter email',
                          },
                          {
                            maxLength: 255,
                            message:
                              popupConfig.tipInvalidEmailText ??
                              "Please don't exceed 255",
                          },
                          {
                            validator: (val, cb) => {
                              if (item.necessary) {
                                let reg = new RegExp(
                                  "^[\\w!#$%&'*+/=?^_`{|}~-]+(?:\\.[\\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\\w](?:[\\w-]*[\\w])?\\.)+[\\w](?:[\\w-]*[\\w])?$",
                                );
                                if (!reg.test(val)) {
                                  cb(
                                    popupConfig.tipInvalidEmailText ??
                                      'Please enter a valid email',
                                  );
                                }
                              }
                              cb();
                            },
                          },
                          ...(item.formSubscribeSignup
                            ? [
                                {
                                  validator: async (value, callback) => {
                                    try {
                                      const res: any = await getContactStatus({
                                        companyUUID,
                                        type: 'email',
                                        email: value,
                                        phone: null,
                                        validate: true,
                                      });
                                      if (
                                        res?.data?.code === 0 &&
                                        res?.data?.data?.subscribeStatus === 1
                                      ) {
                                        callback(
                                          item.tipSubscribeEmailText ??
                                            'You are already subscribed.',
                                        );
                                      }
                                    } catch (error) {
                                      callback('subscribe check error');
                                    }
                                  },
                                },
                              ]
                            : []),
                          ...(item.formRepeatSignup
                            ? [
                                {
                                  validator: async (value, callback) => {
                                    try {
                                      const res: any = await checkEmailOrPhone({
                                        phone: null,
                                        companyUUID,
                                        formId: id,
                                        email: value,
                                      });
                                      if (res?.data?.code !== 0) {
                                        callback(res?.data?.message);
                                      }
                                    } catch (error) {
                                      callback('email check error');
                                    }
                                  },
                                },
                              ]
                            : []),
                        ]}
                      >
                        <Input
                          className={'cartx-embedded-form-input'}
                          onFocus={(e) => {
                            console.log('onFocus', e.target.value);
                            //数据上报
                            formEventV2({
                              event: 'popup_start_typing',
                              triggerEventType,
                              formId: id,
                              formName: name,
                              step: 0,
                              templateId: popupConfig.templateId,
                              inputType: item.type,
                            })
                              .then((response) => {
                                // console.log('数据上报成功')
                              })
                              .catch((e) => {});
                          }}
                          onBlur={(e) => {
                            console.log('onBlur', e.target.value);
                            formEventV2({
                              event: 'popup_stop_typing',
                              triggerEventType,
                              formId: id,
                              formName: name,
                              step: 1,
                              templateId: popupConfig?.templateId,
                              inputType: item.type,
                              remark: e.target.value,
                            })
                              .then((response) => {
                                // console.log('数据上报成功')
                              })
                              .catch((e) => {});
                          }}
                          placeholder={item.placeholder}
                        />
                      </Form.Item>
                    </div>
                  );
                } else if (item.type === 'Mobile Number') {
                  return (
                    <div
                      className="cartx-form-wrapper cartx-form-wrapper-phone"
                      id="cartx-form-mobile-number-embedded"
                      key={'form' + index}
                      style={{
                        display: 'flex',
                        gap: '0',
                        position: 'relative',
                      }}
                    >
                      <Form.Item
                        style={{ display: 'none', marginBottom: 0 }}
                        field="code"
                      >
                        <Select style={{}}></Select>
                      </Form.Item>
                      <Form.Item
                        field={
                          [shopify[30], xike[529]].includes(companyUUID)
                            ? 'phoneCountry'
                            : 'country'
                        }
                        style={{
                          marginBottom: 0,
                          flex: '0 0 auto',
                          width: '100px',
                        }}
                        key={
                          ([shopify[30], xike[529]].includes(companyUUID)
                            ? 'phoneCountry'
                            : 'country') + index
                        }
                        rules={[
                          {
                            required: item.necessary,
                            message:
                              popupConfig?.tipEmptyPhoneText ??
                              'Please enter Mobile Number',
                          },
                          {
                            validator: (val, cb) => {
                              cb();
                            },
                          },
                        ]}
                      >
                        <Select
                          placeholder="Please select ..."
                          onChange={(v) => {
                            // 国家选择变化时，自动更新代码
                            const selectedItem = codeList.find(
                              (item) => item.country === v,
                            );
                            if (selectedItem && selectedItem.code) {
                              form.setFieldValue('code', selectedItem.code);
                            }
                          }}
                          className={
                            'cartx-embedded-form-input phone-code-flag-embedded'
                          }
                          dropdownRender={(menu) => {
                            return (
                              <div
                                className={
                                  triggerProps.position === 'bl'
                                    ? 'cartx-phone-menu cartx-phone-menu-short'
                                    : 'cartx-phone-menu'
                                }
                              >
                                {menu}
                              </div>
                            );
                          }}
                          onVisibleChange={(visible) => {
                            if (visible) {
                              const elem = document.querySelector(
                                `#cartx-embedded-popup-${id} #cartx-form-mobile-number-embedded`,
                              ) as HTMLElement;
                              log(
                                'onVisibleChange visible=%s width=%s',
                                visible,
                                elem?.offsetWidth,
                              );
                              setPopupPhoneWidth(elem?.offsetWidth);
                            }
                          }}
                          renderFormat={(option, value) => {
                            return (
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <img
                                  src={`${getCompanyStaticUrl()}Public/cartsee/formImage/${(
                                    ([shopify[30], xike[529]].includes(
                                      companyUUID,
                                    )
                                      ? phoneCountry
                                      : country) ?? ''
                                  )
                                    .trim()
                                    .toLocaleLowerCase()}.svg`}
                                  alt={`${code}_${[shopify[30], xike[529]].includes(companyUUID) ? phoneCountry : country}`}
                                  style={{
                                    width: '20px',
                                    height: '15px',
                                    aspectRatio: '4 / 3',
                                    borderRadius: '2px',
                                    objectFit: 'cover',
                                  }}
                                />
                                <span>{code}</span>
                              </span>
                            );
                          }}
                          triggerProps={{
                            autoAlignPopupWidth: false,
                            autoAlignPopupMinWidth: true,
                            position: 'bl',
                            getPopupContainer: () =>
                              document.querySelector(
                                '.phone-code-flag-embedded',
                              ) || document.body,
                            style: {
                              width: popupPhoneWidth,
                            },
                            autoFitPopupAlign: false,
                            autoFixPopupAlign: false,
                            ...triggerProps,
                          }}
                          unmountOnExit={false}
                        >
                          {codeList
                            ?.filter(
                              (item) =>
                                item.country === (country || phoneCountry),
                            )
                            .map((codeItem, idx) => {
                              return (
                                <Select.Option
                                  key={`${codeItem.code}_${codeItem.country}_${idx}`}
                                  value={codeItem.country}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '6px 0',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <div
                                      style={{
                                        border: '0.5px solid #F0F0F0',
                                        width: '25px',
                                        height: '18px',
                                        lineHeight: '18px',
                                        flexShrink: 0,
                                        fontSize: 0,
                                      }}
                                    >
                                      <img
                                        src={codeItem.src}
                                        alt={`${codeItem.code}_${codeItem.country}_${idx}`}
                                        style={{
                                          width: '100%',
                                          aspectRatio: '4 / 3',
                                          borderRadius: '2px',
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        marginLeft: '6px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: '12px',
                                        lineHeight: '24px',
                                        fontWeight: 400,
                                      }}
                                    >
                                      {codeItem.countryName}
                                    </div>
                                    <div
                                      style={{
                                        marginLeft: '4px',
                                        flexShrink: 0,
                                        color: '#A1A0AA',
                                        fontSize: '12px',
                                        lineHeight: '24px',
                                        fontWeight: 400,
                                      }}
                                    >
                                      {codeItem.code}
                                    </div>
                                  </div>
                                </Select.Option>
                              );
                            })}
                          {codeList
                            ?.filter(
                              (item) =>
                                item.country !== (country || phoneCountry),
                            )
                            .map((codeItem, idx) => {
                              return (
                                <Select.Option
                                  key={`${codeItem.code}_${codeItem.country}_${idx}`}
                                  value={codeItem.country}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '6px 0',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <div
                                      style={{
                                        border: '0.5px solid #F0F0F0',
                                        width: '25px',
                                        height: '18px',
                                        lineHeight: '18px',
                                        flexShrink: 0,
                                        fontSize: 0,
                                      }}
                                    >
                                      <img
                                        src={codeItem.src}
                                        alt={`${codeItem.code}_${codeItem.country}_${idx}`}
                                        style={{
                                          width: '100%',
                                          aspectRatio: '4 / 3',
                                          borderRadius: '2px',
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        marginLeft: '6px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: '12px',
                                        lineHeight: '24px',
                                        fontWeight: 400,
                                      }}
                                    >
                                      {codeItem.countryName}
                                    </div>
                                    <div
                                      style={{
                                        marginLeft: '4px',
                                        flexShrink: 0,
                                        color: '#A1A0AA',
                                        fontSize: '12px',
                                        lineHeight: '24px',
                                        fontWeight: 400,
                                      }}
                                    >
                                      {codeItem.code}
                                    </div>
                                  </div>
                                </Select.Option>
                              );
                            })}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        field="Number"
                        style={{ marginBottom: 0, flex: 1 }}
                        key={'Number' + index}
                        validateTrigger="onSubmit"
                        rules={[
                          {
                            required: item.necessary,
                            message:
                              popupConfig?.tipEmptyPhoneText ??
                              'Enter Mobile Number',
                          },
                          {
                            maxLength: 255,
                            message:
                              popupConfig?.tipInvalidPhoneText ??
                              "Please don't exceed 255",
                          },
                          {
                            validator: (val, cb) => {
                              if (
                                val &&
                                code === '+1' &&
                                !/^[0-9]{10}$/.test(val)
                              ) {
                                cb(
                                  popupConfig?.tipInvalidPhoneText ??
                                    'Invalid phone number',
                                );
                              } else if (val && !/^[0-9]{8,20}$/.test(val)) {
                                formEventV2({
                                  event: 'popup_phone_invalid',
                                  triggerEventType,
                                  formId: id,
                                  formName: name,
                                  step: step,
                                  templateId: popupConfig?.templateId,
                                  code: code,
                                  remark: val,
                                })
                                  .then((response) => {
                                    // console.log('数据上报成功')
                                  })
                                  .catch((e) => {});
                                // cb(popupConfig?.tipInvalidPhoneText ?? 'phone number is invalid.');
                              } else {
                                cb();
                              }
                            },
                          },
                          ...(item.formSubscribeSignup
                            ? [
                                {
                                  validator: async (value, callback) => {
                                    try {
                                      const res: any = await getContactStatus({
                                        companyUUID,
                                        type: 'phone',
                                        email: null,
                                        phone: code + value,
                                        validate: true,
                                      });
                                      if (
                                        res?.data?.code === 0 &&
                                        res?.data?.data?.smsSubscribeStatus ===
                                          1
                                      ) {
                                        callback(
                                          item.tipSubscribePhoneText ??
                                            'You are already subscribed.',
                                        );
                                      }
                                    } catch (error) {
                                      callback('subscribe check error');
                                    }
                                  },
                                },
                              ]
                            : []),
                          ...(item.formRepeatSignup
                            ? [
                                {
                                  validator: async (value, callback) => {
                                    try {
                                      const res: any = await checkEmailOrPhone({
                                        email: null,
                                        companyUUID,
                                        formId: id,
                                        phone: code + value,
                                      });
                                      if (res?.data?.code !== 0) {
                                        callback(res?.data?.message);
                                      } else {
                                        callback();
                                      }
                                    } catch (error) {
                                      callback('phone check error');
                                    }
                                  },
                                },
                              ]
                            : []),
                        ]}
                      >
                        <Input
                          className={
                            'cartx-embedded-form-input cartx-embedded-form-input-phone'
                          }
                          onChange={(val) => {
                            //数据上报
                            if (val && teleInputFlag) {
                              teleInputFlag = false;
                            }
                          }}
                          onFocus={(event) => {
                            //数据上报
                            formEventV2({
                              event: 'popup_start_typing',
                              triggerEventType,
                              formId: id,
                              formName: name,
                              step: step,
                              templateId: popupConfig?.templateId,
                              inputType: item.type,
                            })
                              .then((response) => {
                                // console.log('数据上报成功')
                              })
                              .catch((e) => {});
                          }}
                          onBlur={(e) => {
                            formEventV2({
                              event: 'popup_stop_typing',
                              triggerEventType,
                              formId: id,
                              formName: name,
                              step: step,
                              templateId: popupConfig?.templateId,
                              inputType: item.type,
                              remark: e.target.value,
                            })
                              .then((response) => {
                                // console.log('数据上报成功')
                              })
                              .catch((e) => {});
                          }}
                          placeholder={
                            item.placeholder ||
                            (item.necessary ? '*Phone Number' : 'Phone Number')
                          }
                        />
                      </Form.Item>
                    </div>
                  );
                } else if (item.type === '自定义') {
                  if (item.questionType === 'Dropdown') {
                    const dropdownOptions = (item.optionList || []).map(
                      (opt) => ({
                        label: opt.value,
                        value: opt.id,
                      }),
                    );
                    return (
                      <div key={'form' + index}>
                        {item.question && (
                          <div className="cartx-dropdown-title">
                            {item.question}
                          </div>
                        )}
                        <Form.Item
                          className="cartx-form"
                          key={index}
                          label=""
                          field={getCustomFieldName(index)}
                          validateTrigger={'onSubmit'}
                          rules={[
                            {
                              required: item.necessary,
                              message: `Please select ${item.question || 'an option'}`,
                            },
                          ]}
                        >
                          <Select
                            className="cartx-dropdown-select"
                            placeholder={item.placeholder || 'Please select'}
                            options={dropdownOptions}
                            triggerProps={{
                              className: 'cartx-dropdown-popper',
                            }}
                            dropdownMenuClassName="cartx-dropdown-menu"
                          />
                        </Form.Item>
                      </div>
                    );
                  }
                  if (
                    item.questionType === 'Date' &&
                    item.formatString === 'yyyy-MM-dd'
                  ) {
                    return (
                      <div key={'form' + index}>
                        <Form.Item
                          className="cartx-form"
                          label=""
                          field={getCustomFieldName(index)}
                          validateTrigger={'onSubmit'}
                          rules={[
                            {
                              required: item.necessary,
                              message: `Please enter Date`,
                            },
                          ]}
                        >
                          <DatePicker
                            className={'cartx-embedded-form-input'}
                            showTime={false}
                            showNowBtn={false}
                            format={(value) => `${value.format('YYYY-MM-DD')}`}
                            placeholder={item.question}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                    );
                  }
                  if (
                    item.questionType === 'Date' &&
                    item.formatString === 'MM-dd'
                  ) {
                    return (
                      <div key={'form' + index}>
                        <Form.Item
                          className="cartx-form"
                          label=""
                          field={getCustomFieldName(index)}
                          validateTrigger={'onSubmit'}
                          rules={[
                            {
                              required: item.necessary,
                              message: 'Please enter Date',
                            },
                          ]}
                        >
                          <MonthDatePicker
                            className={'cartx-embedded-form-input'}
                            placeholder={item.question}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                    );
                  }
                  if (item.optionList && item.optionList[0]) {
                    const optionItems = item.optionList.map((opt) => ({
                      label: opt.value,
                      value: opt.id,
                    }));
                    return (
                      <Form.Item
                        className="cartx-form"
                        key={index}
                        label={
                          <span className="cartx-question">
                            {item.question}
                          </span>
                        }
                        validateTrigger={'onSubmit'}
                        rules={[
                          {
                            required: item.necessary,
                            message: 'Please enter the form',
                          },
                        ]}
                        field={getCustomFieldName(index)}
                      >
                        {item.questionType === 'Single' ? (
                          <Radio.Group
                            size="small"
                            className="cartx-option-columns"
                            options={optionItems}
                          />
                        ) : item.questionType === 'Multiple' ? (
                          <Checkbox.Group
                            className="cartx-option-columns"
                            options={optionItems}
                          />
                        ) : null}
                      </Form.Item>
                    );
                  }
                }
                return null;
              })}
            </Form>
            {/* 主按钮 */}
            <div
              onClick={handleFormButtonClick}
              className="cartx-embedded-form-button"
              style={{
                color: getStyleMapValue('cartx-b-button', 'color'),
                fontFamily: getStyleMapValue('cartx-b-button', 'font-family'),
                background:
                  getStyleMapValue('cartx-b-button', 'background', true) ||
                  getStyleMapValue('cartx-b-button', 'background'),
                borderStyle: getStyleMapValue('cartx-b-button', 'border-style'),
                borderColor: getStyleMapValue('cartx-b-button', 'border-color'),
                borderRadius: getStyleMapValue(
                  'cartx-b-button',
                  'border-radius',
                ),
              }}
            >
              {stateSettings.texts.mainButton}
            </div>
          </div>
        )}

        {(activePageSetting === 'start' || isMiddlePage(activePageSetting)) &&
          ['form', 'button'].includes(settings?.rules?.privacyPosition) && (
            <PrivacyBlock />
          )}

        {settings.rules.couponCodeShow &&
          activePageSetting === settings.rules.couponCodePosition &&
          settings.styles.styleMap.hasOwnProperty('cartx-b-coupon-prompt') && (
            <div
              className="cartx-embedded-coupon-prompt"
              style={{
                color: settings.styles.styleMap['cartx-b-coupon-prompt'].color,
                fontFamily:
                  settings.styles.styleMap['cartx-b-coupon-prompt'][
                    'font-family'
                  ],
                fontSize:
                  settings.styles.styleMap['cartx-b-coupon-prompt'][
                    'font-size'
                  ],
                textAlign:
                  settings.styles.styleMap['cartx-b-coupon-prompt'][
                    'text-align'
                  ],
              }}
            >
              {settings.texts.couponPrompt}
            </div>
          )}

        {settings.rules.couponCodeShow &&
          activePageSetting === settings.rules.couponCodePosition && (
            <div
              className={
                settings.rules.couponCodeType
                  ? `cartx-embedded-coupon-${settings.rules.couponCodeType}`
                  : ''
              }
              style={{
                color:
                  settings.styles.styleMap[
                    `cartx-b-complete-${settings.rules.couponCodeType}`
                  ].color,
                borderRadius:
                  settings.styles.styleMap[
                    `cartx-b-complete-${settings.rules.couponCodeType}`
                  ]['border-radius'],
                borderColor:
                  settings.styles.styleMap[
                    `cartx-b-complete-${settings.rules.couponCodeType}`
                  ]['border-color'],
                background:
                  settings.styles.styleMap[
                    `cartx-b-show .cartx-b-complete-${settings.rules.couponCodeType}`
                  ]?.background ||
                  settings.styles.styleMap[
                    `cartx-b-complete-${settings.rules.couponCodeType}`
                  ].background,
              }}
            >
              <div
                className={
                  settings.rules.couponCodeType
                    ? `cartx-embedded-coupon-text-${settings.rules.couponCodeType}`
                    : ''
                }
                style={{
                  borderRadius:
                    settings.rules.couponCodeType === '1'
                      ? settings.styles.styleMap['cartx-b-complete-text-1'][
                          'border-radius'
                        ]
                      : '',
                  color:
                    settings.styles.styleMap[
                      `cartx-b-complete-${settings.rules.couponCodeType}`
                    ].color,
                }}
              >
                {settings.rules.couponCode}
              </div>
              <div
                className={
                  settings.rules.couponCodeType
                    ? `cartx-embedded-coupon-copy-${settings.rules.couponCodeType}`
                    : ''
                }
                style={{
                  color:
                    settings.styles.styleMap[
                      `cartx-b-complete-${settings.rules.couponCodeType}`
                    ].color,
                }}
                onClick={handleCouponCopy}
              >
                {popupConfig.couponCopyText}
              </div>
              <div
                className={
                  settings.rules.couponCodeType
                    ? `cartx-embedded-coupon-icon-${settings.rules.couponCodeType}`
                    : ''
                }
                onClick={handleCouponCopy}
              >
                <svg
                  className="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                >
                  <path d="M274.285714 274.285714v-190.171428A47.542857 47.542857 0 0 1 321.828571 36.571429h618.057143a47.542857 47.542857 0 0 1 47.542857 47.542857V702.171429a47.542857 47.542857 0 0 1-47.542857 47.542857h-190.171428v189.805714a47.835429 47.835429 0 0 1-47.908572 47.908571h-617.325714a47.835429 47.835429 0 0 1-47.908571-47.908571l0.146285-617.325714c0-26.477714 21.357714-47.908571 47.908572-47.908572H274.285714z m87.332572-1.243428h341.577143a46.811429 46.811429 0 0 1 46.811428 46.811428v337.261715h145.042286V128.585143H361.618286v144.457143z m-238.153143 87.04v540.525714H662.674286v-540.525714H123.465143z"></path>
                </svg>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default EmbeddedPopup;
